import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAccessControl, Client } from '@/hooks/useAccessControl';
import { Button } from '@/components/ui/button';
import { LogIn, LogOut, PlusCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import VersioningSystem from './VersioningSystem';
import InlineReleaseForm from './InlineReleaseForm';
import { useReleases } from '@/hooks/useReleases';
import { Release } from '@/types';
import { NativeDialog, NativeDialogContent, NativeDialogHeader, NativeDialogTitle } from '@/components/ui/native-dialog';
import { 
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

const ClientReleases: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { isAdmin, isLoggedIn, logout } = useAccessControl();
  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [webflowClientId, setWebflowClientId] = useState<string | null>(null);
  const { addRelease, fetchReleases, releases, updateRelease, deleteRelease, isLoading: releasesLoading } = useReleases(clientId || '');
  const [showReleaseForm, setShowReleaseForm] = useState(false);
  const [isCreatingRelease, setIsCreatingRelease] = useState(false);

  // Check for webflow client ID
  useEffect(() => {
    const storedClientId = localStorage.getItem('webflow_versioning_client_id');
    console.log('[ClientReleases] Webflow client ID:', storedClientId);
    setWebflowClientId(storedClientId);
  }, []);
  
  // Log the clientId from URL params for debugging
  useEffect(() => {
    console.log('[ClientReleases] Client ID from URL params:', clientId);
  }, [clientId]);
  
  // Access control: Redirect non-admin users if they try to access a client that isn't theirs
  useEffect(() => {
    if (!isLoading && isLoggedIn && !isAdmin && clientId) {
      const userClientId = localStorage.getItem('webflow_release_access') 
        ? JSON.parse(localStorage.getItem('webflow_release_access') || '{}').clientId 
        : null;
      
      console.log('[ClientReleases] Access check - User client ID:', userClientId, 'Requested client ID:', clientId);
      
      // If the user is not an admin and is trying to access a client that isn't theirs
      if (userClientId && userClientId !== clientId) {
        console.log('[ClientReleases] Access denied - redirecting to user\'s client');
        toast({
          title: "Access Restricted",
          description: "You only have access to your assigned client",
          variant: "destructive"
        });
        navigate(`/clients/${userClientId}`);
      }
    }
  }, [isLoading, isLoggedIn, isAdmin, clientId, navigate, toast]);

  useEffect(() => {
    const fetchClient = async () => {
      if (!clientId) return;
      
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('id', clientId)
          .single();
        
        if (error || !data) {
          throw error || new Error('Client not found');
        }
        
        setClient(data as Client);
      } catch (error) {
        console.error('Error fetching client:', error);
        toast({
          title: 'Error',
          description: 'Failed to load client information',
          variant: 'destructive',
        });
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchClient();
  }, [clientId, navigate, toast]);

  // Handle login button click
  const handleLoginClick = () => {
    // Store client ID in localStorage before navigating to access page
    if (clientId) {
      localStorage.setItem('webflow_versioning_client_id', clientId);
    }
    // For HashRouter, we need to use navigate with the hash-based path
    navigate('/access');
  };

  // Handle logout button click
  const handleLogoutClick = () => {
    logout();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out"
    });
  };

  const handleBackClick = () => {
    // If admin coming from webflow client site, don't go to admin panel
    if (isAdmin && !webflowClientId) {
      navigate('/admin');
    } else {
      navigate('/');
    }
  };

  // Handle create release
  const handleCreateRelease = async (newRelease: Omit<Release, 'id'>) => {
    console.log('[ClientReleases.handleCreateRelease] Received newRelease from form:', JSON.stringify(newRelease, null, 2));
    console.log('[ClientReleases.handleCreateRelease] clientId from useParams:', clientId);
    
    // Validate clientId is present and not empty
    if (!clientId || clientId.trim() === '') {
      console.error('[ClientReleases] Cannot create release: clientId is missing or empty');
      toast({
        title: "Error",
        description: "Cannot create release: missing client information",
        variant: "destructive"
      });
      return;
    }
    
    // Check if we're already in the process of creating a release
    if (isCreatingRelease) {
      console.warn('[ClientReleases] Already creating a release, ignoring duplicate request');
      return;
    }

    // Create a new release object with the client ID from URL params
    const releaseWithClientId = {
      ...newRelease,
      clientId: clientId, // Explicitly set clientId from URL params
      createdAt: new Date(), // Ensure we have a valid creation date
    };
    
    setIsCreatingRelease(true);
    console.log('[ClientReleases.handleCreateRelease] releaseWithClientId to be sent to addRelease:', JSON.stringify(releaseWithClientId, null, 2));
    console.log('[ClientReleases.handleCreateRelease] Client ID being used:', clientId);
    try {
      console.log('[ClientReleases] Creating new release with client ID:', clientId);
      const result = await addRelease(releaseWithClientId);
      console.log('[ClientReleases] Release created successfully with ID:', result);
      
      // Hide the form
      setShowReleaseForm(false);
      
      // Refresh releases after creating a new one
      await fetchReleases();
      
      toast({
        title: "Release created",
        description: "Your new release has been created successfully."
      });
    } catch (error) {
      console.error('Error creating release:', error);
      toast({
        title: "Error",
        description: "Failed to create release. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreatingRelease(false);
    }
  };
  
  // Handle cancel form
  const handleCancelForm = () => {
    setShowReleaseForm(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  // Determine if we should show the back button:
  // Only show for admin users who didn't come from webflow (no webflowClientId)
  const showBackButton = isAdmin && !webflowClientId;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        {/* Left side with breadcrumbs */}
        <div>
          <Breadcrumb>
            <BreadcrumbList>
              {isAdmin && (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/admin">Admin</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                </>
              )}
              <BreadcrumbItem>
                <BreadcrumbPage>{client?.name} Releases</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Right side with buttons */}
        <div className="flex items-center gap-2">
          {isLoggedIn && (
            <>
              <Button 
                className="flex items-center gap-2" 
                disabled={isCreatingRelease}
                onClick={() => setShowReleaseForm(true)}
              >
                <PlusCircle size={16} />
                <span>{isCreatingRelease ? 'Creating...' : 'New Release'}</span>
              </Button>
            </>
          )}
          
          {isLoggedIn ? (
            <Button onClick={handleLogoutClick} variant="outline" className="flex items-center gap-2">
              <LogOut size={16} />
              <span>Log Out</span>
            </Button>
          ) : (
            <Button onClick={handleLoginClick} className="flex items-center gap-2">
              <LogIn size={16} />
              <span>Log In</span>
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6">
        <VersioningSystem 
          clientId={clientId || ''} 
          hideHeader={true}
          releases={releases} 
          updateRelease={updateRelease}
          deleteRelease={deleteRelease}
          isLoading={releasesLoading}
        />
      </div>

      {/* Dialog */}
      <NativeDialog 
        open={showReleaseForm} 
        onOpenChange={(open) => {
          if (!open) {
            handleCancelForm();
          }
          setShowReleaseForm(open);
        }} 
        size="3xl"
      >
        <NativeDialogContent>
          <NativeDialogHeader>
            <NativeDialogTitle>Create New Release</NativeDialogTitle>
          </NativeDialogHeader>
          <InlineReleaseForm 
            onSubmit={handleCreateRelease} 
            onCancel={handleCancelForm} 
          />
        </NativeDialogContent>
      </NativeDialog>
    </div>
  );
};

export default ClientReleases;
